import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { auth } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Users, Plus, Trash2, Edit3, X, Check, Shield, ShieldOff,
  Eye, EyeOff, AlertTriangle, Clock, Loader2, CheckSquare, Square
} from 'lucide-react';

interface UserData {
  id: number; username: string; role: string; permissions: string[];
  display_name: string; is_active: boolean; created_at: string;
}
interface PageOption { key: string; label: string; }

const UserManagement: React.FC = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState<UserData[]>([]);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [rolePresets, setRolePresets] = useState<Record<string, string[]>>({});
  const [activityLog, setActivityLog] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState<UserData | null>(null);
  const [showActivity, setShowActivity] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Form state
  const [formUsername, setFormUsername] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formRole, setFormRole] = useState('operations');
  const [formPermissions, setFormPermissions] = useState<Set<string>>(new Set());
  const [showPassword, setShowPassword] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, pagesRes] = await Promise.all([auth.getUsers(), auth.getAvailablePages()]);
      setUsers(usersRes.data);
      setPages(pagesRes.data.pages || []);
      setRolePresets(pagesRes.data.role_presets || {});
    } catch (e: any) { setError(e.response?.data?.detail || 'Failed to load'); }
    setLoading(false);
  };

  const loadActivity = async () => {
    try {
      const res = await auth.getActivityLog(50);
      setActivityLog(res.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const showMsg = (msg: string, type: 'ok' | 'err' = 'ok') => {
    if (type === 'ok') { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); }
    else { setError(msg); setTimeout(() => setError(''), 4000); }
  };

  const resetForm = () => {
    setFormUsername(''); setFormPassword(''); setFormDisplayName('');
    setFormRole('operations'); setFormPermissions(new Set()); setShowAdd(false); setEditUser(null);
  };

  const handleRoleChange = (role: string) => {
    setFormRole(role);
    if (role !== 'custom' && rolePresets[role]) {
      setFormPermissions(new Set(rolePresets[role]));
    }
  };

  const togglePermission = (key: string) => {
    setFormPermissions(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    if (formRole !== 'custom') setFormRole('custom');
  };

  const handleCreate = async () => {
    if (!formUsername || !formPassword) { showMsg('Username and password required', 'err'); return; }
    setSaving(true);
    try {
      await auth.createUser({
        username: formUsername, password: formPassword, role: formRole,
        permissions: formRole === 'custom' ? Array.from(formPermissions) : undefined,
        display_name: formDisplayName || formUsername,
      });
      showMsg('User created'); resetForm(); load();
    } catch (e: any) { showMsg(e.response?.data?.detail || 'Failed', 'err'); }
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await auth.updateUser(editUser.id, {
        role: formRole,
        permissions: formRole === 'custom' ? Array.from(formPermissions) : undefined,
        display_name: formDisplayName,
        new_password: formPassword || undefined,
      });
      showMsg('User updated'); resetForm(); load();
    } catch (e: any) { showMsg(e.response?.data?.detail || 'Failed', 'err'); }
    setSaving(false);
  };

  const handleToggleActive = async (user: UserData) => {
    try {
      await auth.updateUser(user.id, { is_active: !user.is_active });
      showMsg(user.is_active ? 'User deactivated' : 'User activated'); load();
    } catch (e: any) { showMsg(e.response?.data?.detail || 'Failed', 'err'); }
  };

  const handleDelete = async (user: UserData) => {
    if (!window.confirm(`Delete user "${user.username}"? This cannot be undone.`)) return;
    try {
      await auth.deleteUser(user.id);
      showMsg('User deleted'); load();
    } catch (e: any) { showMsg(e.response?.data?.detail || 'Failed', 'err'); }
  };

  const startEdit = (user: UserData) => {
    setEditUser(user);
    setFormUsername(user.username);
    setFormDisplayName(user.display_name || '');
    setFormRole(user.role);
    setFormPassword('');
    setFormPermissions(new Set(user.permissions || []));
    setShowAdd(true);
  };

  if (!isAdmin) {
    return (
      <Layout title="User Management" subtitle="Admin access required">
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--accent-red)', fontSize: 15 }}>
          <ShieldOff size={40} style={{ marginBottom: 12, opacity: 0.5 }} /><br />
          You don't have permission to access this page.
        </div>
      </Layout>
    );
  }

  const roleColors: Record<string, { bg: string; text: string }> = {
    admin: { bg: 'var(--accent-red-soft)', text: 'var(--accent-red)' },
    operations: { bg: 'var(--accent-blue-soft)', text: 'var(--accent-blue)' },
    custom: { bg: 'var(--accent-purple-soft)', text: 'var(--accent-purple)' },
  };

  return (
    <Layout title="User Management" subtitle="Create and manage team access">
      {success && <div className="animate-in" style={{ padding: '10px 14px', background: 'var(--accent-green-soft)', border: '1px solid rgba(45,212,160,0.2)', borderRadius: 'var(--radius-sm)', color: 'var(--accent-green)', fontSize: 13, fontWeight: 500, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}><Check size={14} /> {success}</div>}
      {error && <div className="animate-in" style={{ padding: '10px 14px', background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.2)', borderRadius: 'var(--radius-sm)', color: 'var(--accent-red)', fontSize: 13, fontWeight: 500, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}><AlertTriangle size={14} /> {error}</div>}

      {/* Header */}
      <div className="animate-in" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 22px' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>Users</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)' }}>{users.length}</div>
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 22px' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>Active</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent-green)' }}>{users.filter(u => u.is_active).length}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setShowActivity(true); loadActivity(); }} style={{
            padding: '9px 16px', background: 'var(--accent-purple-soft)', border: '1px solid rgba(157,122,255,0.15)',
            borderRadius: 'var(--radius-sm)', color: 'var(--accent-purple)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}><Clock size={14} /> Activity Log</button>
          <button onClick={() => { resetForm(); setShowAdd(true); }} style={{
            padding: '9px 18px', background: 'var(--accent-blue)', border: 'none',
            borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}><Plus size={14} /> Add User</button>
        </div>
      </div>

      {/* Users List */}
      <div className="animate-in-1" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /></div>
        ) : users.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No users found</div>
        ) : (
          <div className="table-responsive">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['User', 'Role', 'Pages Access', 'Status', 'Created', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const rc = roleColors[u.role] || roleColors.custom;
                  return (
                    <tr key={u.id} style={{ borderBottom: '1px solid var(--border)', opacity: u.is_active ? 1 : 0.5 }}>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{u.display_name || u.username}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>@{u.username}</div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: rc.bg, color: rc.text, textTransform: 'capitalize' }}>{u.role}</span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>
                        {u.role === 'admin' ? 'Full access' : `${u.permissions?.length || 0} pages`}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: u.is_active ? 'var(--accent-green-soft)' : 'var(--accent-red-soft)',
                          color: u.is_active ? 'var(--accent-green)' : 'var(--accent-red)',
                        }}>{u.is_active ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button onClick={() => startEdit(u)} title="Edit" style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Edit3 size={12} color="var(--text-secondary)" /></button>
                          <button onClick={() => handleToggleActive(u)} title={u.is_active ? 'Deactivate' : 'Activate'} style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {u.is_active ? <ShieldOff size={12} color="var(--accent-amber)" /> : <Shield size={12} color="var(--accent-green)" />}
                          </button>
                          <button onClick={() => handleDelete(u)} title="Delete" style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={12} color="var(--accent-red)" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═══ ADD/EDIT USER MODAL ═══ */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) resetForm(); }}>
          <div className="modal-content" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '28px', width: 540, maxHeight: '85vh', overflowY: 'auto', boxShadow: 'var(--shadow-elevated)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{editUser ? 'Edit User' : 'Create New User'}</h2>
              <button onClick={resetForm} style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={14} color="var(--text-muted)" /></button>
            </div>

            {/* Username */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 6, fontWeight: 600 }}>Username</label>
              <input value={formUsername} onChange={e => setFormUsername(e.target.value)} disabled={!!editUser}
                placeholder="e.g. rahul" style={{ width: '100%', padding: '10px 14px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', opacity: editUser ? 0.5 : 1 }} />
            </div>

            {/* Display Name */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 6, fontWeight: 600 }}>Display Name</label>
              <input value={formDisplayName} onChange={e => setFormDisplayName(e.target.value)}
                placeholder="e.g. Rahul Sharma" style={{ width: '100%', padding: '10px 14px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 6, fontWeight: 600 }}>
                {editUser ? 'New Password (leave blank to keep current)' : 'Password'}
              </label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} value={formPassword} onChange={e => setFormPassword(e.target.value)}
                  placeholder={editUser ? 'Leave blank to keep current' : 'Min 6 characters'}
                  style={{ width: '100%', padding: '10px 40px 10px 14px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Role */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8, fontWeight: 600 }}>Role</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  { key: 'admin', label: 'Admin', desc: 'Full access', color: 'var(--accent-red)' },
                  { key: 'operations', label: 'Operations', desc: 'Inventory & dispatch', color: 'var(--accent-blue)' },
                  { key: 'custom', label: 'Custom', desc: 'Pick pages', color: 'var(--accent-purple)' },
                ].map(r => (
                  <button key={r.key} onClick={() => handleRoleChange(r.key)} style={{
                    flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'center',
                    border: `1px solid ${formRole === r.key ? r.color : 'var(--border)'}`,
                    background: formRole === r.key ? `${r.color}15` : 'var(--bg-elevated)',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: formRole === r.key ? r.color : 'var(--text-primary)' }}>{r.label}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Granular Permissions */}
            {(formRole === 'custom' || formRole === 'operations') && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8, fontWeight: 600 }}>Page Access</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {pages.map(p => {
                    const checked = formPermissions.has(p.key);
                    return (
                      <button key={p.key} onClick={() => togglePermission(p.key)} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                        background: checked ? 'var(--accent-blue-soft)' : 'var(--bg-elevated)',
                        border: `1px solid ${checked ? 'rgba(79,125,249,0.3)' : 'var(--border)'}`,
                        borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'left',
                      }}>
                        {checked ? <CheckSquare size={14} color="var(--accent-blue)" /> : <Square size={14} color="var(--text-muted)" />}
                        <span style={{ fontSize: 12.5, fontWeight: checked ? 600 : 400, color: checked ? 'var(--accent-blue)' : 'var(--text-secondary)' }}>{p.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={resetForm} style={{ padding: '9px 20px', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              <button onClick={editUser ? handleUpdate : handleCreate} disabled={saving} style={{
                padding: '9px 20px', background: 'var(--accent-blue)', color: '#fff', border: 'none',
                borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>{saving ? 'Saving...' : editUser ? 'Update User' : 'Create User'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ACTIVITY LOG MODAL ═══ */}
      {showActivity && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setShowActivity(false); }}>
          <div className="modal-content" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 0, width: 600, maxHeight: '80vh', overflowY: 'auto', boxShadow: 'var(--shadow-elevated)' }}>
            <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 2, borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0' }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>Activity Log</h2>
              <button onClick={() => setShowActivity(false)} style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={14} color="var(--text-muted)" /></button>
            </div>
            <div style={{ padding: '16px 24px 24px' }}>
              {activityLog.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>No activity recorded yet</div>
              ) : activityLog.map(log => (
                <div key={log.id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-blue-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Users size={14} color="var(--accent-blue)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                      <strong>{log.username}</strong> <span style={{ color: 'var(--text-secondary)' }}>{log.action.replace(/_/g, ' ')}</span>
                      {log.details && <span style={{ color: 'var(--text-muted)' }}> — {log.details}</span>}
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>
                      {log.created_at ? new Date(log.created_at).toLocaleString('en-IN') : ''}
                      {log.ip_address && ` · ${log.ip_address}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Layout>
  );
};

export default UserManagement;
